/**
 * Utility functions for calculating scorecard completion progress across solutions
 */

import type { Participant, Solution, CriteriaGroup } from '@/types/evaluation.types';
import { storageService } from '@/services/local-storage';

/**
 * Result of scorecard completion calculation
 */
export interface ScorecardCompletionInfo {
  /** Overall completion percentage (0-100) */
  percentage: number;
  /** Number of criteria scored across all solutions */
  scoredCount: number;
  /** Total number of criteria across all solutions the participant needs to score */
  totalCount: number;
  /** Per-solution breakdown */
  solutions: {
    solutionId: string;
    percentage: number;
    scored: number;
    total: number;
  }[];
}

/**
 * Calculates the overall scorecard completion for a participant across ALL solutions
 * in an evaluation, respecting their criteria group assignments.
 *
 * Calculation Logic:
 * 1. Determine which criteria groups the participant can score:
 *    - If criteriaGroupAssignments is null → all groups
 *    - If criteriaGroupAssignments is [] → no groups (Viewer)
 *    - Otherwise → only assigned groups
 * 2. For each solution, count how many criteria in assigned groups have been scored
 * 3. Calculate overall percentage across all solutions
 *
 * @param participant - The participant to calculate completion for
 * @param evaluationId - The evaluation ID
 * @param solutions - List of solutions in the evaluation
 * @param criteriaGroups - All criteria groups in the evaluation
 * @returns Completion information including overall percentage and per-solution breakdown
 *
 * @remarks
 * This differs from single-solution progress in that it aggregates across all solutions.
 * A participant with 3 solutions to score needs to complete criteria for ALL solutions.
 */
export function calculateScorecardCompletion(
  participant: Participant,
  evaluationId: string,
  solutions: Solution[],
  criteriaGroups: CriteriaGroup[]
): ScorecardCompletionInfo {
  // Determine which groups this participant can score
  const assignedGroupIds = participant.criteriaGroupAssignments;

  // Filter criteria groups based on assignments
  let applicableGroups: CriteriaGroup[];

  if (assignedGroupIds === null) {
    // null means all groups are assigned (typical for Host)
    applicableGroups = criteriaGroups;
  } else if (assignedGroupIds.length === 0) {
    // Empty array means no groups assigned (Viewer - should not score)
    applicableGroups = [];
  } else {
    // Only assigned groups
    applicableGroups = criteriaGroups.filter((group) =>
      assignedGroupIds.includes(group.id)
    );
  }

  // Get all criteria IDs that this participant should score
  const applicableCriteriaIds = new Set(
    applicableGroups.flatMap((group) => group.criteria.map((c) => c.id))
  );

  // If no criteria are applicable, return 0% completion
  if (applicableCriteriaIds.size === 0) {
    return {
      percentage: 0,
      scoredCount: 0,
      totalCount: 0,
      solutions: solutions.map((solution) => ({
        solutionId: solution.id,
        percentage: 0,
        scored: 0,
        total: 0,
      })),
    };
  }

  // Calculate per-solution completion for this specific participant
  const solutionBreakdown = solutions.map((solution) => {
    // Get the scorecard for this participant and solution
    const scorecard = storageService.getScorecardBySolution(evaluationId, solution.id, participant.id);

    if (!scorecard) {
      // No scorecard exists for this participant and solution yet
      return {
        solutionId: solution.id,
        percentage: 0,
        scored: 0,
        total: applicableCriteriaIds.size,
      };
    }

    // Count how many applicable criteria have been scored
    const scoredApplicable = scorecard.scores.filter(
      (score) =>
        applicableCriteriaIds.has(score.criteriaId) &&
        score.score !== null &&
        score.score !== undefined
    ).length;

    const percentage =
      applicableCriteriaIds.size > 0
        ? Math.round((scoredApplicable / applicableCriteriaIds.size) * 100)
        : 0;

    return {
      solutionId: solution.id,
      percentage,
      scored: scoredApplicable,
      total: applicableCriteriaIds.size,
    };
  });

  // Calculate overall completion across all solutions
  const totalCriteriaNeeded = applicableCriteriaIds.size * solutions.length;
  const totalScored = solutionBreakdown.reduce((sum, s) => sum + s.scored, 0);
  const overallPercentage =
    totalCriteriaNeeded > 0
      ? Math.round((totalScored / totalCriteriaNeeded) * 100)
      : 0;

  return {
    percentage: overallPercentage,
    scoredCount: totalScored,
    totalCount: totalCriteriaNeeded,
    solutions: solutionBreakdown,
  };
}
